/**
 * Professional Invoice Generation
 * Matches the Perfume Stories invoice format exactly.
 */

export const generateInvoiceHTML = (bill, settings = {}) => {
  const companyName = settings.companyName || 'The Perfume Stories (Pvt) Ltd.';
  const companyAddress = settings.companyAddress || '';
  const companyEmail = settings.companyEmail || '';
  const companyPhone = settings.companyPhone || '';
  const companyLogo = settings.companyLogo || '';

  const fmt = (n) =>
    parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Per-product batch discounts
  const perProductBatchDiscounts = (bill.items || [])
    .map(item => {
      if (!item.hasBatchDiscounts || !item.batchBreakdown) return null;
      const amt = item.batchBreakdown.reduce((s, b) => {
        if (!b.hasDiscount) return s;
        return s + (b.originalPrice - b.price) * b.quantity;
      }, 0);
      const pct = item.batchBreakdown.find(b => b.hasDiscount)?.discount || 0;
      return amt > 0 ? { name: item.name, amount: amt, pct } : null;
    })
    .filter(Boolean);

  const batchDiscountAmt = perProductBatchDiscounts.reduce((s, d) => s + d.amount, 0);

  const rawSubtotal = bill.subtotal
    ? bill.subtotal + batchDiscountAmt
    : (bill.items || []).reduce((s, i) => s + i.price * i.quantity, 0);
  const subtotal = rawSubtotal;

  const globalDiscountAmt = bill.discount?.amount || 0;
  const loyaltyDiscountAmt = bill.loyaltyDiscount?.amount || 0;
  const kokoChargeAmt = bill.kokoCharge?.amount || 0;
  const mintPayChargeAmt = bill.mintPayCharge?.amount || 0;
  const deliveryChargeAmt = bill.paymentType === 'COD' ? (bill.deliveryCharge || 0) : 0;
  const total = bill.total || (subtotal - batchDiscountAmt - globalDiscountAmt - loyaltyDiscountAmt + kokoChargeAmt + mintPayChargeAmt + deliveryChargeAmt);
  const balanceDue = (bill.status === 'Paid' || bill.status === 'Koko' || bill.status === 'MintPay') ? 0 : total;
  const paymentMade = (bill.status === 'Paid' || bill.status === 'Koko' || bill.status === 'MintPay') ? total : 0;
  // Cash/Card = paid immediately — no need to show payment breakdown on invoice
  const isCashPayment = bill.paymentType === 'Cash' || bill.paymentType === 'Card';
  const showPaymentRows = !isCashPayment;

  const addressLines = companyAddress
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => `<div>${l}</div>`)
    .join('');

  const itemRows = bill.items
    .map(
      (item, idx) => `
      <tr>
        <td class="col-num">${idx + 1}</td>
        <td class="col-desc">${item.name}</td>
        <td class="col-qty">${parseFloat(item.quantity).toFixed(2)}<br><span class="unit">pcs</span></td>
        <td class="col-rate">${fmt(item.price)}</td>
        <td class="col-amt">${fmt(item.price * item.quantity)}</td>
      </tr>`
    )
    .join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Invoice ${bill.invoiceNumber || bill.id}</title>
  <style>
    @page { size: A4; margin: 14mm 14mm 18mm 14mm; }
    @media print {
      @page { margin: 14mm 14mm 18mm 14mm; }
      body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page-footer { position: fixed; bottom: 0; width: 100%; }
    }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 13px;
      color: #1a1a1a;
      background: #fff;
    }

    /* ── TOP HEADER ── */
    .top-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 32px;
    }
    .logo-box {
      width: 200px;
      height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      flex-shrink: 0;
    }
    .logo-box img {
      width: 200px;
      height: 200px;
      object-fit: cover;
      display: block;
    }
    .logo-text {
      font-size: 22px;
      font-weight: 700;
      color: #1a1a1a;
      line-height: 1.3;
    }
    .invoice-title-block {
      text-align: right;
    }
    .invoice-title {
      font-size: 42px;
      font-weight: 300;
      letter-spacing: 2px;
      color: #1a1a1a;
      line-height: 1;
      margin-bottom: 4px;
    }
    .invoice-num {
      font-size: 13px;
      font-weight: 700;
      color: #1a1a1a;
      margin-bottom: 12px;
    }
    .balance-label {
      font-size: 11px;
      color: #666;
      margin-bottom: 2px;
    }
    .balance-amount {
      font-size: 22px;
      font-weight: 700;
      color: #1a1a1a;
    }

    /* ── COMPANY BLOCK ── */
    .company-block {
      font-size: 12px;
      line-height: 1.5;
      color: #333;
    }
    .company-block strong {
      font-size: 13px;
      color: #1a1a1a;
      display: block;
      margin-bottom: 2px;
    }

    /* ── DATES ── */
    .dates-block {
      text-align: right;
      font-size: 12px;
    }
    .dates-block .date-row {
      display: flex;
      justify-content: flex-end;
      gap: 20px;
      margin-bottom: 6px;
    }
    .dates-block .date-label { color: #666; }
    .dates-block .date-val { color: #1a1a1a; font-weight: 500; min-width: 110px; text-align: right; }

    /* ── BILL TO ── */
    .bill-to {
      margin-bottom: 20px;
    }
    .bill-to-label {
      font-size: 11px;
      color: #888;
      margin-bottom: 2px;
    }
    .bill-to-name {
      font-size: 14px;
      font-weight: 700;
      color: #1a1a1a;
    }

    /* ── ITEMS TABLE ── */
    table.items {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 0;
    }
    table.items thead tr {
      background: #2d2d2d;
      color: #fff;
    }
    table.items thead th {
      padding: 10px 8px;
      font-size: 12px;
      font-weight: 600;
      text-transform: none;
      letter-spacing: 0;
      border: none;
    }
    table.items tbody tr {
      border-bottom: 1px solid #ebebeb;
    }
    table.items tbody td {
      padding: 7px 8px;
      font-size: 12px;
      color: #1a1a1a;
      vertical-align: top;
      border: none;
    }
    .unit { font-size: 10px; color: #888; }
    .col-num  { width: 4%;  text-align: center; }
    .col-desc { width: 52%; }
    .col-qty  { width: 12%; text-align: center; }
    .col-rate { width: 16%; text-align: right; }
    .col-amt  { width: 16%; text-align: right; font-weight: 600; }
    th.col-num  { text-align: center; }
    th.col-qty  { text-align: center; }
    th.col-rate { text-align: right; }
    th.col-amt  { text-align: right; }

    /* ── TOTALS ── */
    .totals-wrapper {
      display: flex;
      justify-content: flex-end;
      margin-top: 0;
      border-top: 1px solid #ebebeb;
    }
    .totals-table {
      width: 320px;
      font-size: 12px;
    }
    .totals-table td {
      padding: 7px 8px;
      color: #333;
    }
    .totals-table .t-label { text-align: left; }
    .totals-table .t-val   { text-align: right; font-weight: 500; }
    .totals-table .total-row td {
      font-size: 13px;
      font-weight: 700;
      color: #1a1a1a;
      border-top: 1px solid #e0e0e0;
      border-bottom: 1px solid #e0e0e0;
      padding: 9px 8px;
    }
    .totals-table .payment-row td { color: #1a1a1a; }
    .totals-table .discount-row td { color: #1a1a1a; }
    .totals-table .koko-row td { color: #1a1a1a; }
    .totals-table .balance-row td {
      background: #f5f5f5;
      font-size: 14px;
      font-weight: 700;
      color: #1a1a1a;
      padding: 10px 8px;
    }

    /* ── NOTES ── */
    .notes-section {
      margin-top: 28px;
      font-size: 12px;
    }
    .notes-label { color: #888; margin-bottom: 4px; }
    .notes-text  { color: #333; }

    /* ── FOOTER ── */
    .page-footer {
      margin-top: 32px;
      padding-top: 10px;
      border-top: 1px solid #e0e0e0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 10px;
      color: #aaa;
    }
  </style>
</head>
<body>
  <!-- TOP HEADER: logo left, invoice title right -->
  <div class="top-header">
    <div class="logo-box">
      ${companyLogo
        ? `<img src="${companyLogo}" alt="Logo" style="width:200px;height:200px;object-fit:cover;display:block;" />`
        : `<div class="logo-text"><span style="color:#c41e3a;">Perfume</span> Stories</div>`}
    </div>
    <div class="invoice-title-block">
      <div class="invoice-title">INVOICE</div>
      <div class="invoice-num"># ${bill.invoiceNumber || bill.id}</div>
      ${showPaymentRows ? `
      <div class="balance-label">Balance Due</div>
      <div class="balance-amount">LKR ${fmt(balanceDue)}</div>
      ` : ''}
    </div>
  </div>

  <!-- COMPANY INFO (above divider) -->
  <div style="margin-bottom: 24px; padding-bottom: 20px; border-bottom: 1px solid #e0e0e0;">
    <div class="company-block">
      <strong>${companyName}</strong>
      ${addressLines}
      ${companyEmail ? `<div>${companyEmail}</div>` : ''}
      ${companyPhone ? `<div>${companyPhone}</div>` : ''}
    </div>
  </div>

  <!-- BILL TO + DATES (below divider, side by side) -->
  <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom: 28px;">
    <div class="bill-to">
      <div class="bill-to-label">Bill To</div>
      <div class="bill-to-name">${bill.customer || '—'}</div>
      ${bill.paymentType ? `<div style="font-size:12px;color:#555;margin-top:2px;">${bill.paymentType}</div>` : ''}
    </div>
    <div class="dates-block">
      <div class="date-row">
        <span class="date-label">Invoice Date :</span>
        <span class="date-val">${bill.date}</span>
      </div>
      <div class="date-row">
        <span class="date-label">Terms :</span>
        <span class="date-val">Due on Receipt</span>
      </div>
      <div class="date-row">
        <span class="date-label">Due Date :</span>
        <span class="date-val">${bill.date}</span>
      </div>
    </div>
  </div>

  <!-- ITEMS TABLE -->
  <table class="items">
    <thead>
      <tr>
        <th class="col-num">#</th>
        <th class="col-desc">Item &amp; Description</th>
        <th class="col-qty">Qty</th>
        <th class="col-rate">Rate</th>
        <th class="col-amt">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${itemRows}
    </tbody>
  </table>

  <!-- TOTALS -->
  <div class="totals-wrapper">
    <table class="totals-table">
      <tr>
        <td class="t-label">Sub Total</td>
        <td class="t-val">${fmt(subtotal)}</td>
      </tr>
      ${perProductBatchDiscounts.map(d => `
      <tr class="discount-row">
        <td class="t-label">${d.name} — Discount (${d.pct}%)</td>
        <td class="t-val">- ${fmt(d.amount)}</td>
      </tr>`).join('')}
      ${globalDiscountAmt > 0 ? `
      <tr class="discount-row">
        <td class="t-label">Discount${bill.discount?.type === 'percentage' ? ` (${bill.discount.value}%)` : ''}</td>
        <td class="t-val">- ${fmt(globalDiscountAmt)}</td>
      </tr>` : ''}
      ${loyaltyDiscountAmt > 0 ? `
      <tr class="discount-row">
        <td class="t-label">Loyalty Discount${bill.loyaltyDiscount?.discountValue ? ` (${bill.loyaltyDiscount.discountValue}%)` : ''}</td>
        <td class="t-val">- ${fmt(loyaltyDiscountAmt)}</td>
      </tr>` : ''}
      ${kokoChargeAmt > 0 ? `
      <tr class="koko-row">
        <td class="t-label">Koko Service Charge (${bill.kokoCharge?.percentage || 12}%)</td>
        <td class="t-val">+ ${fmt(kokoChargeAmt)}</td>
      </tr>` : ''}
      ${mintPayChargeAmt > 0 ? `
      <tr class="koko-row">
        <td class="t-label">MintPay Service Charge (${bill.mintPayCharge?.percentage || 12}%)</td>
        <td class="t-val">+ ${fmt(mintPayChargeAmt)}</td>
      </tr>` : ''}
      ${deliveryChargeAmt > 0 ? `
      <tr class="koko-row">
        <td class="t-label">Delivery Charge</td>
        <td class="t-val">+ ${fmt(deliveryChargeAmt)}</td>
      </tr>` : ''}
      <tr class="total-row">
        <td class="t-label">Total</td>
        <td class="t-val">LKR ${fmt(total)}</td>
      </tr>
      ${showPaymentRows && paymentMade > 0 ? `
      <tr class="payment-row">
        <td class="t-label">Payment Made</td>
        <td class="t-val">(-) ${fmt(paymentMade)}</td>
      </tr>` : ''}
      ${showPaymentRows ? `
      <tr class="balance-row">
        <td class="t-label">Balance Due</td>
        <td class="t-val">LKR ${fmt(balanceDue)}</td>
      </tr>` : ''}
    </table>
  </div>

  <!-- NOTES -->
  ${bill.note ? `
  <div class="notes-section">
    <div class="notes-label">Notes</div>
    <div class="notes-text">${bill.note}</div>
  </div>` : ''}

  <!-- FOOTER -->
  <div class="page-footer">
    <span>Perfume Stories</span>
    <span>This is a computer generated document.</span>
  </div>
</body>
</html>`;
};

/** Open browser print dialog */
export const printProfessionalInvoice = async (bill, settings = {}) => {
  const html = generateInvoiceHTML(bill, settings);
  const win = window.open('', '_blank', 'width=850,height=650');
  if (!win) throw new Error('Please allow popups to print invoices');
  win.document.write(html);
  win.document.close();
  win.onload = () => {
    const imgs = win.document.images;
    if (imgs.length === 0) {
      setTimeout(() => win.print(), 300);
      return;
    }
    let loaded = 0;
    const tryPrint = () => { if (++loaded === imgs.length) setTimeout(() => win.print(), 300); };
    Array.from(imgs).forEach((img) => {
      if (img.complete) tryPrint();
      else { img.onload = tryPrint; img.onerror = tryPrint; }
    });
  };
};

/** Download as PDF via html2canvas + jsPDF */
export const downloadProfessionalInvoicePDF = async (bill, settings = {}) => {
  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);

  const html = generateInvoiceHTML(bill, settings);

  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, {
    position: 'fixed', right: '0', bottom: '0',
    width: '794px', height: '1123px',
    border: '0', opacity: '0', pointerEvents: 'none', zIndex: '-1',
  });
  document.body.appendChild(iframe);

  const iDoc = iframe.contentDocument || iframe.contentWindow.document;
  iDoc.open(); iDoc.write(html); iDoc.close();

  await new Promise((resolve) => {
    const imgs = iframe.contentWindow.document.images;
    if (imgs.length === 0) { setTimeout(resolve, 400); return; }
    let loaded = 0;
    const done = () => { if (++loaded === imgs.length) setTimeout(resolve, 400); };
    Array.from(imgs).forEach((img) => {
      if (img.complete && img.naturalHeight !== 0) done();
      else { img.onload = done; img.onerror = done; }
    });
  });

  const el = iframe.contentWindow.document.body;
  const canvas = await html2canvas(el, {
    backgroundColor: '#ffffff', scale: 1.5,
    useCORS: true, logging: false, width: 794, windowWidth: 794,
  });

  const pdf = new jsPDF('p', 'mm', 'a4');
  const imgData = canvas.toDataURL('image/jpeg', 0.92);
  const imgH = (canvas.height * 210) / canvas.width;
  pdf.addImage(imgData, 'JPEG', 0, 0, 210, Math.min(imgH, 297));
  pdf.save(`Invoice_${bill.invoiceNumber || bill.id}.pdf`);

  document.body.removeChild(iframe);
};
